// ═══════════════════════════════════════════════════════════════════
//  modules/documents/CloudModule.js
//  CloudModule
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { btnBack, btnGhost, btnPrimary, iconBtn, inp } from "../../composants/styles";
import { API_BASE } from "../../socle/api";
import { fmtSize } from "../../socle/format";
import { THEME } from "../../socle/theme";
import { DOC_FILE_CATEGORIES, DOC_FILE_CAT_LABELS } from "./constantes";
import { FileMetaForm } from "./FileMetaForm";
import { catColor, fileIcon } from "./utils";

// ═══════════════ 2) CLOUD OFFICIEL ═══════════════
export function CloudModule({ token, onBack }) {
  const [files,setFiles]=useState([]);
  const [loading,setLoading]=useState(true);
  const [q,setQ]=useState(""); const [cat,setCat]=useState(""); const [statut,setStatut]=useState("actif");
  const [uploading,setUploading]=useState(false);
  const [editing,setEditing]=useState(null);
  const [hint,setHint]=useState("");
  const [lastCreated,setLastCreated]=useState(null);
  const fileRef=useRef(null);

  const load=async()=>{
    setLoading(true);
    const params=new URLSearchParams(); if(q)params.set("q",q); if(cat)params.set("categorie",cat); if(statut)params.set("statut",statut);
    try{ const r=await fetch(`${API_BASE}/api/documents/files?${params}`,{headers:{Authorization:`Bearer ${token}`}}); const d=await r.json(); setFiles(Array.isArray(d)?d:[]); }
    catch(e){ console.error(e); setFiles([]); }
    setLoading(false);
  };
  // Volontaire : la recherche « q » n'est PAS dans les dépendances, sinon on
  // relancerait une requête à chaque frappe. Elle est appliquée par le bouton.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ load(); },[cat,statut]);

  const onPick=async(e)=>{
    const file=e.target.files?.[0]; if(!file) return;
    setUploading(true); setLastCreated(null);
    const fd=new FormData(); fd.append("file",file); if(hint.trim())fd.append("hint",hint.trim());
    try{
      const r=await fetch(`${API_BASE}/api/documents/files`,{method:"POST",headers:{Authorization:`Bearer ${token}`},body:fd});
      if(!r.ok)throw new Error();
      const row=await r.json();
      const c=row.created||{};
      const msgs=[];
      if(c.transaction) msgs.push("💎 dépense ajoutée au budget");
      if(c.contrat) msgs.push("📜 fiche contrat créée");
      if(c.domaine) msgs.push("🌐 domaine enregistré");
      if(c.marque) msgs.push("🏷️ marque/logo enregistré");
      setLastCreated(msgs.length?msgs.join(" · "):null);
      setHint("");
      await load();
    }
    catch{ alert("Upload impossible (vérifie que le backend tourne)."); }
    setUploading(false); if(fileRef.current)fileRef.current.value="";
  };
  const download=async(file)=>{
    try{ const r=await fetch(`${API_BASE}/api/documents/files/${file.id}/download`,{headers:{Authorization:`Bearer ${token}`}}); if(!r.ok)throw new Error(); const blob=await r.blob(); const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=file.nom_origine; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); }
    catch{ alert("Téléchargement indisponible (étape 4)."); }
  };
  const remove=async(id)=>{ if(!window.confirm("Mettre ce fichier à la corbeille ?"))return; try{ await fetch(`${API_BASE}/api/documents/files/${id}`,{method:"DELETE",headers:{Authorization:`Bearer ${token}`}}); load(); }catch(e){console.error(e);} };
  const reclassify=async(id)=>{ try{ await fetch(`${API_BASE}/api/documents/files/${id}/reclassify`,{method:"POST",headers:{Authorization:`Bearer ${token}`}}); load(); }catch{ alert("Reclassement indisponible (étape 4)."); } };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <button onClick={onBack} style={btnBack}>← Documents</button>
        <h2 style={{color:THEME.text,fontSize:18,fontWeight:800,margin:0}}>☁️ Cloud officiel</h2>
        <input ref={fileRef} type="file" onChange={onPick} style={{display:"none"}} />
        <button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{marginLeft:"auto",...btnPrimary,opacity:uploading?0.6:1}}>{uploading?"Analyse IA…":"⬆ Déposer un fichier"}</button>
      </div>

      <div style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:12,padding:12}}>
        <label style={{color:THEME.textMuted,fontSize:11,fontWeight:600}}>💡 Précisions pour l'IA (optionnel) — aide au classement et à l'extraction</label>
        <textarea value={hint} onChange={(e)=>setHint(e.target.value)} placeholder="ex : Facture d'hébergement mensuelle payée par Gaëlle, renouvellement le 5 de chaque mois" style={{...inp,minHeight:46,resize:"vertical",marginTop:6}} />
        <div style={{color:THEME.textDim,fontSize:11,marginTop:4}}>Saisis tes précisions puis clique sur « Déposer un fichier ». Elles seront prises en compte pour ce dépôt.</div>
      </div>

      {lastCreated && (
        <div style={{background:THEME.emeraldDim,border:`1px solid ${THEME.emerald}`,color:THEME.emerald,borderRadius:10,padding:"10px 14px",fontSize:13}}>
          Créé automatiquement : {lastCreated}
        </div>
      )}

      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&load()} placeholder="Rechercher (nom, résumé, tags)…" style={{...inp,maxWidth:280}} />
        <select value={cat} onChange={(e)=>setCat(e.target.value)} style={{...inp,maxWidth:180}}><option value="">Toutes catégories</option>{DOC_FILE_CATEGORIES.map((c)=><option key={c} value={c}>{DOC_FILE_CAT_LABELS[c]}</option>)}</select>
        <select value={statut} onChange={(e)=>setStatut(e.target.value)} style={{...inp,maxWidth:150}}><option value="actif">Actifs</option><option value="archive">Archivés</option><option value="corbeille">Corbeille</option><option value="">Tous</option></select>
        <button onClick={load} style={btnGhost}>🔍</button>
      </div>

      {loading ? <div style={{color:THEME.textMuted,padding:20}}>Chargement…</div> : files.length===0 ? (
        <div style={{background:THEME.panel,border:`1px dashed ${THEME.border}`,borderRadius:14,padding:40,textAlign:"center",color:THEME.textMuted}}>
          <div style={{fontSize:34,marginBottom:8}}>☁️</div>Aucun fichier. Dépose un document : l'IA le classera automatiquement (catégorie, résumé, tags).
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {files.map((file)=>(
            <div key={file.id} style={{background:THEME.panel,border:`1px solid ${THEME.border}`,borderRadius:12,padding:14,display:"flex",gap:14,alignItems:"flex-start"}}>
              <div style={{fontSize:26}}>{fileIcon(file.extension)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{color:THEME.text,fontSize:14,fontWeight:700,wordBreak:"break-all"}}>{file.nom_origine}</span>
                  {file.categorie && <span style={{fontSize:10,fontWeight:700,color:catColor(file.categorie),border:`1px solid ${catColor(file.categorie)}`,borderRadius:6,padding:"1px 7px"}}>{DOC_FILE_CAT_LABELS[file.categorie]||file.categorie}</span>}
                  {file.ia_status==="en_attente" && <span style={{fontSize:10,color:THEME.amber}}>⏳ classement…</span>}
                  {file.ia_status==="echec" && <span style={{fontSize:10,color:THEME.red}}>⚠ IA échouée</span>}
                </div>
                {file.resume && <div style={{color:THEME.textMuted,fontSize:12,marginTop:4,lineHeight:1.4}}>{file.resume}</div>}
                {file.tags && <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:6}}>{String(file.tags).split(",").filter(Boolean).map((t,i)=><span key={i} style={{fontSize:10,color:THEME.textMuted,background:THEME.surface,borderRadius:5,padding:"1px 6px"}}>#{t.trim()}</span>)}</div>}
                <div style={{color:THEME.textDim,fontSize:11,marginTop:6}}>{fmtSize(file.taille_octets)} · v{file.version||1} · {(file.created_at||"").slice(0,10)}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <button onClick={()=>download(file)} title="Télécharger" style={iconBtn}>⬇</button>
                <button onClick={()=>setEditing(file)} title="Éditer le classement" style={iconBtn}>✏️</button>
                <button onClick={()=>reclassify(file.id)} title="Relancer l'IA" style={iconBtn}>🤖</button>
                <button onClick={()=>remove(file.id)} title="Corbeille" style={iconBtn}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <FileMetaForm token={token} file={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load();}} />}
    </div>
  );
}
